import { useMemo, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/ui/carousel";
import type { PriceCategory } from "@/types/vehicleCard";
import { useLanguage } from "@/context/LanguageContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { auctionsLabels } from "@/i18n/labels/auctionsLabels";
import { getPriceIntervalI18nKey } from "@/admin/utils/ratingIntervals";
import { cn } from "@/lib/utils";

const colorMap: Record<string, string> = {
  success: "#16a34a",
  info: "#0284c7",
  warning: "#ca8a04",
  destructive: "#dc2626",
  error: "#dc2626",
};

interface ListingPriceIntervalsSectionProps {
  priceCategories: PriceCategory[];
}

function PriceIntervalLabel({
  label,
  color,
  compact,
}: {
  label: string;
  color: string;
  compact?: boolean;
}) {
  return (
    <div
      className={cn(
        "font-semibold text-center",
        compact
          ? "text-[11px] leading-tight whitespace-nowrap tracking-tight w-full px-0"
          : "text-[10px] sm:text-[11px] leading-tight line-clamp-2 px-1",
      )}
      style={{ color: colorMap[color] ?? "#666666" }}
    >
      {label}
    </div>
  );
}

function PriceIntervalRange({
  category,
  locale,
  compact,
}: {
  category: PriceCategory;
  locale: string;
  compact?: boolean;
}) {
  const lower = category.lowerBound.toLocaleString(locale);
  const upper = category.upperBound.toLocaleString(locale);

  if (compact) {
    return (
      <div className="font-medium text-foreground tabular-nums text-center text-[10px] leading-[1.2] max-w-full">
        <div>{lower} -</div>
        <div>{upper} :-</div>
      </div>
    );
  }

  return (
    <div className="font-medium text-foreground tabular-nums text-center text-[10px] sm:text-[11px] leading-tight whitespace-nowrap">
      {lower} - {upper} :-
    </div>
  );
}

/**
 * Compact, read-only price intervals section for CarDetails.
 * Mobile: five intervals with edge peek, clipped inside the card.
 * Desktop: grid of all intervals.
 */
export function ListingPriceIntervalsSection({
  priceCategories,
}: ListingPriceIntervalsSectionProps) {
  const { language } = useLanguage();
  const isMobile = useIsMobile();
  const labels = auctionsLabels(language);
  const locale = language === "en" ? "en-GB" : "sv-SE";
  const marketDataLabel =
    labels.priceIntervals?.marketDataSource ?? "Inhämtad marknadsdata";

  const sortedCategories = useMemo(
    () => [...priceCategories].sort((a, b) => (b.dots ?? 0) - (a.dots ?? 0)),
    [priceCategories],
  );

  const fairPriceIndex = useMemo(() => {
    const byDots = sortedCategories.findIndex((c) => c.dots === 3);
    if (byDots >= 0) return byDots;
    const fairLabel = labels.priceIntervals?.fairPrice;
    if (!fairLabel) return Math.floor(sortedCategories.length / 2);
    return sortedCategories.findIndex((c) => {
      const key = getPriceIntervalI18nKey(c.label);
      const display =
        key &&
        labels.priceIntervals?.[key as keyof typeof labels.priceIntervals]
          ? labels.priceIntervals[key as keyof typeof labels.priceIntervals]
          : c.label;
      return display === fairLabel;
    });
  }, [sortedCategories, labels.priceIntervals]);

  const startIndex =
    fairPriceIndex >= 0 ? fairPriceIndex : Math.floor(sortedCategories.length / 2);

  const [carouselApi, setCarouselApi] = useState<CarouselApi | null>(null);
  const [activeIndex, setActiveIndex] = useState(startIndex);

  useEffect(() => {
    if (!carouselApi || !isMobile) return;

    const scrollToFair = () => {
      carouselApi.scrollTo(startIndex, false);
      setActiveIndex(carouselApi.selectedScrollSnap());
    };

    scrollToFair();
    carouselApi.on("reInit", scrollToFair);
    const onSelect = () => setActiveIndex(carouselApi.selectedScrollSnap());
    carouselApi.on("select", onSelect);

    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(scrollToFair);
    });

    return () => {
      cancelAnimationFrame(raf);
      carouselApi.off("reInit", scrollToFair);
      carouselApi.off("select", onSelect);
    };
  }, [carouselApi, isMobile, startIndex]);

  if (!sortedCategories.length) return null;

  const getDisplayLabel = (category: PriceCategory) => {
    const i18nKey = getPriceIntervalI18nKey(category.label);
    return i18nKey &&
      labels.priceIntervals?.[i18nKey as keyof typeof labels.priceIntervals]
      ? labels.priceIntervals[i18nKey as keyof typeof labels.priceIntervals]
      : category.label;
  };

  const canScrollPrev = activeIndex > 0;
  const canScrollNext = activeIndex < sortedCategories.length - 1;

  return (
    <Card className="w-full shadow-sm overflow-hidden">
      <CardContent className="px-2 py-2 sm:px-4 sm:py-3 md:px-5 lg:px-6 overflow-hidden">
        <p className="text-xs sm:text-xs text-muted-foreground text-center mb-1.5">
          {marketDataLabel}
        </p>
        {isMobile ? (
          <div className="relative mobile-price-intervals-carousel native-price-intervals-carousel overflow-hidden">
            <div
              className={cn(
                "absolute left-0 top-1/2 z-10 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full bg-neutral-700/35 backdrop-blur-[2px] pointer-events-none",
                !canScrollPrev && "opacity-60",
              )}
              aria-hidden
            >
              <ChevronLeft
                className={cn(
                  "h-3.5 w-3.5",
                  canScrollPrev ? "text-foreground/90" : "text-muted-foreground/40",
                )}
              />
            </div>
            <div
              className={cn(
                "absolute right-0 top-1/2 z-10 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full bg-neutral-700/35 backdrop-blur-[2px] pointer-events-none",
                !canScrollNext && "opacity-60",
              )}
              aria-hidden
            >
              <ChevronRight
                className={cn(
                  "h-3.5 w-3.5",
                  canScrollNext ? "text-foreground/90" : "text-muted-foreground/40",
                )}
              />
            </div>
            <Carousel
              setApi={setCarouselApi}
              opts={{
                align: "center",
                loop: false,
                containScroll: "trimSnaps",
                startIndex,
                dragFree: false,
              }}
              className="relative w-full px-1 overflow-hidden"
            >
              <CarouselContent className="ml-1.5">
                {sortedCategories.map((category, index) => {
                  const displayLabel = getDisplayLabel(category);
                  const isEdge =
                    index === 0 || index === sortedCategories.length - 1;
                  const isActive = index === activeIndex;
                  return (
                    <CarouselItem
                      key={index}
                      className="pl-0 basis-[22%] min-w-0 shrink-0"
                    >
                      <div
                        className={cn(
                          "flex flex-col items-center text-center gap-0 min-w-0 w-full max-w-full overflow-hidden px-0 py-0 transition-all duration-200",
                          isActive && "opacity-100",
                          !isActive && isEdge && "opacity-50 scale-[0.94]",
                          !isActive && !isEdge && "opacity-75 scale-[0.97]",
                        )}
                      >
                        <PriceIntervalLabel
                          label={displayLabel}
                          color={category.color}
                          compact
                        />
                        <PriceIntervalRange
                          category={category}
                          locale={locale}
                          compact
                        />
                      </div>
                    </CarouselItem>
                  );
                })}
              </CarouselContent>
            </Carousel>
          </div>
        ) : (
          <div className="grid grid-cols-5 gap-x-3 sm:gap-x-4 md:gap-x-5 lg:gap-x-6 gap-y-2">
            {sortedCategories.map((category, index) => {
              const displayLabel = getDisplayLabel(category);
              return (
                <div
                  key={index}
                  className="flex flex-col items-center text-center space-y-0.5 min-w-0 px-0.5 sm:px-1"
                >
                  <PriceIntervalLabel
                    label={displayLabel}
                    color={category.color}
                  />
                  <PriceIntervalRange
                    category={category}
                    locale={locale}
                  />
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
